import { Component, inject } from "@angular/core";
import { UiKitModule } from "@app/ui-kit.module";
import { Gas, LocalizeGasNamePipe } from "@domain/gas/gas";
import { GasService } from "@domain/gas/gas.service";
import { LocalizeProductNamePipe } from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { TransmitterConfiguration } from "@domain/project/configurations/transmitter-configuration";
import { FgdsProject } from "@domain/project/fgds-project";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { ProjectEventType } from "@domain/project/project-event";
import { untilDestroyed } from "@odx/angular/utils";
import { ConfiguredProductComponent } from "@project/floorplanner/floorplan-menu/fgds/configured-product/configured-product.component";
import { FloorplanProductMenuComponent } from "@project/floorplanner/floorplan-menu/fgds/floorplan-product-menu/floorplan-product-menu.component";
import { FloorplanService } from "@project/floorplanner/floorplan.service";
import { ProjectStateService } from "@project/project-state.service";
import { DraggedItemType, FloorplanDragDropService } from "@utils/floorplan-drag-drop.service";
import { forkJoin, map, Observable, of } from "rxjs";

interface TransmitterSubtitles {
  subtitle: string;
  substanceSubtitle: string;
}

@Component({
  selector: "app-floorplan-transmitter-menu",
  templateUrl: "./floorplan-transmitter-menu.component.html",
  styleUrls: ["./floorplan-transmitter-menu.component.scss"],
  imports: [UiKitModule, FloorplanProductMenuComponent, ConfiguredProductComponent],
  providers: [LocalizeGasNamePipe, LocalizeProductNamePipe],
})
export class FloorplanTransmitterMenuComponent {
  private floorplanService = inject(FloorplanService);
  private gasService = inject(GasService);
  private productService = inject(ProductService);
  private projectStateService = inject(ProjectStateService);
  private localizeGasNamePipe = inject(LocalizeGasNamePipe);
  private localizeProductNamePipe = inject(LocalizeProductNamePipe);
  private dragDropService = inject(FloorplanDragDropService);

  protected floorplan: Floorplan;
  protected transmitterSubtitles = new Map<TransmitterConfiguration, Observable<TransmitterSubtitles>>();
  private takeUntilDestroyed = untilDestroyed();

  protected readonly tooltipTextShowMonitoringArea: string = $localize`:@@floorplan.button.showMonitoringArea:Überwachungsbereich einblenden`;
  protected readonly tooltipTextHideMonitoringArea: string = $localize`:@@floorplan.button.hideMonitoringArea:Überwachungsbereich ausblenden`;
  protected readonly tooltipTextTransmitters: string = $localize`:@@global.transmitters:Transmitter`;

  constructor() {
    this.floorplan = this.floorplanService.selectedFloorplan;
    this.transmitters.forEach((transmitter) => {
      this.transmitterSubtitles.set(transmitter, this.subtitles$(transmitter));
    });
    this.projectStateService.projectEvents$.pipe(this.takeUntilDestroyed()).subscribe((event) => {
      if (event.type === ProjectEventType.TRANSMITTER_CONFIGURATION_DELETED) {
        this.transmitterSubtitles.delete(event.subject);
      }
    });
  }

  protected get transmitters(): readonly TransmitterConfiguration[] {
    if (this.floorplan.project instanceof FgdsProject) {
      return this.floorplan.project.transmitters;
    }
    return [];
  }

  protected addTransmitter(transmitter: TransmitterConfiguration) {
    this.floorplanService.addTransmitter(transmitter);
  }

  protected deleteTransmitter(transmitter: TransmitterConfiguration) {
    (this.floorplan.project as FgdsProject).deleteTransmitter(transmitter);
  }

  protected toggleMonitoringAreaVisibility() {
    this.floorplan.floorplanState.monitoringAreasVisible = !this.floorplan.floorplanState.monitoringAreasVisible;
  }

  protected addPlaceholder() {
    this.floorplanService.addTransmitterPlaceholder();
  }

  protected drag(transmitterConfig: TransmitterConfiguration) {
    this.dragDropService.setDragged({ type: DraggedItemType.TRANSMITTER, config: transmitterConfig });
  }

  protected dragPlaceholder() {
    this.dragDropService.setDragged({ type: DraggedItemType.TRANSMITTER_PLACEHOLDER });
  }

  protected updateTransmittersItemTextVisibility(visible: boolean) {
    this.floorplan.floorplanState.updateTransmittersItemTextVisibility(visible);
  }

  private subtitles$(transmitter: TransmitterConfiguration): Observable<TransmitterSubtitles> {
    const gas$ = transmitter.gasId ? this.gasService.getGasById(transmitter.gasId) : of(undefined);
    const sensor$ = transmitter.sensorId ? this.productService.getProductById(transmitter.sensorId) : of(undefined);

    return forkJoin({ gas: gas$, sensor: sensor$ }).pipe(
      map(({ gas, sensor }) => ({
        subtitle: sensor ? this.localizeProductNamePipe.transform(sensor) : "",
        substanceSubtitle: gas ? this.formatSubstanceSubtitle(gas) : "",
      })),
    );
  }

  private formatSubstanceSubtitle(gas: Gas): string {
    const gasName = this.localizeGasNamePipe.transform(gas);
    const rawCasNumber = gas.definition.casNumber;

    if (!rawCasNumber || gasName === rawCasNumber) {
      return gasName;
    }

    const casNumber = rawCasNumber.startsWith("CAS") ? rawCasNumber.substring(3).trim() : rawCasNumber;
    if (!casNumber || gasName === casNumber) {
      return gasName;
    }

    const formattedCasNumber = $localize`:@@floorplan.configuredProduct.substance.casNumber:CAS-Nr.: ${casNumber}`;
    return gasName ? `${gasName}, ${formattedCasNumber}` : formattedCasNumber;
  }
}
